/* Version: 3.5.149 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import 'react';
import PropTypes from 'prop-types';
import Icon from '../Icon/Icon.js';
import { rem } from '../../utils/functions.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { TOOLTIP_VARIANT, TOOLTIP_SIDES } from './constants.js';
import { Root, ToggleButton, Arrow, TooltipWrapper, TooltipContent, TooltipText, CloseButton } from './styledComponents.js';
import useTooltip from './useTooltip.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import useTheme from '../../hooks/useTheme.js';

var Tooltip = function Tooltip(_ref) {
  var id = _ref.id,
    children = _ref.children,
    _ref$side = _ref.side,
    side = _ref$side === undefined ? null : _ref$side,
    ariaLabel = _ref.ariaLabel,
    _ref$title = _ref.title,
    title = _ref$title === undefined ? '' : _ref$title,
    content = _ref.content,
    _ref$bubbleWidth = _ref.bubbleWidth,
    bubbleWidth = _ref$bubbleWidth === undefined ? 300 : _ref$bubbleWidth,
    _ref$hover = _ref.hover,
    hover = _ref$hover === undefined ? true : _ref$hover,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? TOOLTIP_VARIANT.PRIMARY : _ref$variant,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      rootStyle: {},
      toggleButtonStyle: {}
    } : _ref$customStyle,
    _ref$disableAriaForPo = _ref.disableAriaForPopup,
    disableAriaForPopup = _ref$disableAriaForPo === undefined ? false : _ref$disableAriaForPo;
  var theme = useTheme();
  var _safeCustomStyle = safeCustomStyle(customStyle),
    rootStyle = _safeCustomStyle.rootStyle,
    toggleButtonStyle = _safeCustomStyle.toggleButtonStyle;
  var _useTooltip = useTooltip({
      id: id,
      side: side,
      ariaLabel: ariaLabel,
      title: title,
      content: content,
      bubbleWidth: bubbleWidth,
      hover: hover,
      variant: variant,
      disableAriaForPopup: disableAriaForPopup
    }),
    tooltipContent = _useTooltip.tooltipContent,
    showTooltip = _useTooltip.showTooltip,
    showCloseButton = _useTooltip.showCloseButton,
    wrapperProps = _useTooltip.wrapperProps,
    toggleProps = _useTooltip.toggleProps,
    tooltipProps = _useTooltip.tooltipProps,
    arrowProps = _useTooltip.arrowProps,
    closeButtonProps = _useTooltip.closeButtonProps,
    defaultIconProps = _useTooltip.defaultIconProps;
  var iconProps = {
    manulifeName: variant === TOOLTIP_VARIANT.SECONDARY && !showTooltip ? 'question_withcircle' : 'question_withcircle_filled',
    fill: variant === TOOLTIP_VARIANT.PRIMARY || showTooltip ? theme.colorPalette.neutralColors.light.shade_white : theme.colorPalette.primaryColors.main.shade_default,
    secondaryColor: showTooltip ? theme.colorPalette.primaryColors.main.shade_dark_2 : theme.colorPalette.primaryColors.main.shade_default
  };
  return /*#__PURE__*/jsxs(Root, _objectSpread(_objectSpread({
    rootStyle: rootStyle
  }, wrapperProps), {}, {
    children: [/*#__PURE__*/jsx(ToggleButton, _objectSpread(_objectSpread({
      type: "button",
      theme: theme,
      toggleButtonStyle: toggleButtonStyle,
      "data-testid": "toggle-test"
    }, toggleProps), {}, {
      children: children || /*#__PURE__*/jsx(Icon, _objectSpread(_objectSpread({
        "data-testid": "icon-test",
        className: "mux-Tooltip-Icon"
      }, defaultIconProps), iconProps))
    })), showTooltip && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Arrow, _objectSpread({
        "data-testid": "arrow",
        theme: theme
      }, arrowProps)), /*#__PURE__*/jsx(TooltipWrapper, _objectSpread(_objectSpread({
        "data-testid": "wrapper"
      }, tooltipProps), {}, {
        children: /*#__PURE__*/jsxs(TooltipContent, {
          theme: theme,
          children: [/*#__PURE__*/jsx(TooltipText, {
            children: tooltipContent
          }), showCloseButton && /*#__PURE__*/jsx(CloseButton, _objectSpread(_objectSpread({
            type: "button"
          }, closeButtonProps), {}, {
            children: /*#__PURE__*/jsx(Icon, {
              manulifeName: "close",
              fill: theme.colorPalette.neutralColors.light.shade_white,
              color: theme.colorPalette.neutralColors.light.shade_white,
              alt: "close",
              height: rem(12),
              width: rem(12)
            })
          }))]
        })
      }))]
    })]
  }));
};
process.env.NODE_ENV !== "production" ? Tooltip.propTypes = {
  /** DOM element ID */
  id: PropTypes.string,
  /**
   * Element to wrap with tooltip, will render default tooltip icon if no children provided
   */
  children: PropTypes.node,
  /**
   * Position of the nub.
   * Not specifying the side means the tooltip will determine side automatically using best placement for a 3x3 grid.
   * Specifying a nub position should only be done to override positioning behaviour.
   */
  side: PropTypes.oneOf([TOOLTIP_SIDES.BOTTOM, TOOLTIP_SIDES.BOTTOM_LEFT, TOOLTIP_SIDES.BOTTOM_RIGHT, TOOLTIP_SIDES.TOP, TOOLTIP_SIDES.TOP_LEFT, TOOLTIP_SIDES.TOP_RIGHT, TOOLTIP_SIDES.RIGHT // Connected DS only
  ]),
  /** aria-label for tooltip */
  ariaLabel: PropTypes.string,
  /**
   * Text shown inside the tooltip bubble. If `content` prop is provided it will override `title`.
   *
   * **NOTE**: This prop will be deprecated in v4. Please use `content` prop instead.
   */
  title: PropTypes.string,
  /** Content shown inside the tooltip bubble */
  content: PropTypes.node,
  /** Exact width of tooltip bubble (necessary for calculating positioning) */
  bubbleWidth: PropTypes.number,
  /**
   * flag to determine if tooltip will have hover functionality for displaying message.
   * Setting hover to false also shows close icon on top right.
   * */
  hover: PropTypes.bool,
  /** Which variant to use if using default icon */
  variant: PropTypes.oneOf([TOOLTIP_VARIANT.PRIMARY, TOOLTIP_VARIANT.SECONDARY]),
  /** Style overrides */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    toggleButtonStyle: PropTypes.object
  }),
  /** Should screen readers ignore the tooltip popup? (used internally by ProgressBar) */
  disableAriaForPopup: PropTypes.bool
} : undefined;
process.env.NODE_ENV !== "production" ? Tooltip.displayName = 'Tooltip' : undefined;

export { Tooltip as default };
